import axios from 'axios';
import type { Project, CreateProjectDto, ProjectsExtendedResponse, NationalCoverageResult, NationalScopeType } from './types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

/**
 * Obtiene el token de autenticación del localStorage
 */
const getAuthToken = (): string | null => {
  const authStorage = localStorage.getItem('auth-storage');
  if (!authStorage) return null;
  const parsed = JSON.parse(authStorage) as { state?: { token?: string } };
  return parsed.state?.token || null;
};

/**
 * DTO para crear proyecto como SuperAdmin (incluye tenantId)
 */
export interface CreateProjectAdminDto extends CreateProjectDto {
  tenantId: string;
}

/**
 * DTO para asignar proyecto a tenant
 */
export interface AssignTenantDto {
  tenantId: string;
}

/**
 * Cliente API para gestión global de proyectos (SuperAdmin)
 *
 * @remarks
 * **Solo accesible para SuperAdmin (@wayline.city)**
 *
 * Endpoints:
 * - GET /admin/projects - Lista TODOS los proyectos (sin filtro de tenant)
 * - GET /admin/projects/:id - Obtiene un proyecto (sin filtro de tenant)
 * - POST /admin/projects - Crea proyecto y lo asigna a un tenant
 * - PATCH /admin/projects/:id - Actualiza proyecto (sin filtro de tenant)
 * - DELETE /admin/projects/:id - Elimina proyecto (sin filtro de tenant)
 * - PATCH /admin/projects/:id/assign-tenant - Reasigna proyecto a otro tenant
 *
 * @example
 * ```typescript
 * // Listar todos los proyectos globalmente
 * const projects = await adminProjectsApi.list();
 *
 * // Crear proyecto para un tenant
 * const project = await adminProjectsApi.create({
 *   name: 'Análisis de Movilidad Urbana',
 *   description: 'Estudio de patrones de movilidad',
 *   bounds: { minLng: -170, minLat: -60, maxLng: -30, maxLat: 75 },
 *   tenantId: 'uuid-del-tenant'
 * });
 *
 * // Reasignar proyecto a otro tenant
 * await adminProjectsApi.assignTenant(projectId, { tenantId: newTenantId });
 * ```
 */
export const adminProjectsApi = {
  /**
   * Obtiene el próximo número de proyecto para un tenant específico
   *
   * @param tenantId - ID del tenant
   * @returns Próximo número de proyecto para ese tenant (1, 2, 3, ..., 99)
   */
  async getNextProjectNumber(tenantId: string): Promise<number> {
    const token = getAuthToken();
    const response = await axios.get<{ nextNumber: number }>(
      `${API_URL}/admin/projects/next-project-number?tenantId=${tenantId}`,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data.nextNumber;
  },

  /**
   * Lista TODOS los proyectos de TODOS los tenants
   *
   * @returns Todos los proyectos (sin filtro de tenant)
   */
  async list(): Promise<Project[]> {
    const token = getAuthToken();
    const response = await axios.get<Project[]>(`${API_URL}/admin/projects`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Lista TODOS los proyectos con información extendida
   *
   * @remarks
   * Incluye estadísticas de pipeline, red, módulos y uso para el panel expandible
   *
   * @returns Proyectos extendidos con resumen estadístico
   */
  async listExtended(): Promise<ProjectsExtendedResponse> {
    const token = getAuthToken();
    const response = await axios.get<ProjectsExtendedResponse>(`${API_URL}/admin/projects/extended`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Obtiene un proyecto por ID (sin restricción de tenant)
   *
   * @param id - ID del proyecto
   * @returns Proyecto encontrado
   */
  async get(id: string): Promise<Project> {
    const token = getAuthToken();
    const response = await axios.get<Project>(`${API_URL}/admin/projects/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Crea un nuevo proyecto y lo asigna a un tenant
   *
   * @param data - Datos del proyecto (incluye tenantId)
   * @returns Proyecto creado
   */
  async create(data: CreateProjectAdminDto): Promise<Project> {
    const token = getAuthToken();
    const response = await axios.post<Project>(`${API_URL}/admin/projects`, data, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Actualiza un proyecto (sin restricción de tenant)
   *
   * @param id - ID del proyecto
   * @param data - Datos a actualizar
   * @returns Proyecto actualizado
   */
  async update(id: string, data: Partial<CreateProjectDto>): Promise<Project> {
    const token = getAuthToken();
    const response = await axios.patch<Project>(`${API_URL}/admin/projects/${id}`, data, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Elimina un proyecto (sin restricción de tenant)
   *
   * @param id - ID del proyecto
   */
  async delete(id: string): Promise<void> {
    const token = getAuthToken();
    await axios.delete(`${API_URL}/admin/projects/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },

  /**
   * Reasigna un proyecto a otro tenant
   *
   * @param id - ID del proyecto
   * @param data - Contiene el nuevo tenantId
   * @returns Proyecto actualizado con nuevo tenant
   */
  async assignTenant(id: string, data: AssignTenantDto): Promise<Project> {
    const token = getAuthToken();
    const response = await axios.patch<Project>(`${API_URL}/admin/projects/${id}/assign-tenant`, data, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Reinicia el pipeline de construcción de un proyecto
   *
   * @param id - ID del proyecto
   * @returns ID del job encolado
   */
  async retryPipeline(id: string): Promise<{ jobId: string }> {
    const token = getAuthToken();
    const response = await axios.post<{ jobId: string }>(`${API_URL}/admin/projects/${id}/retry-pipeline`, {}, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Reintentar solo la fase de sintaxis (sin resetear pipeline)
   *
   * @param id - ID del proyecto
   * @returns ID del job de sintaxis encolado
   */
  async retrySyntax(id: string): Promise<{ jobId: string }> {
    const token = getAuthToken();
    const response = await axios.post<{ jobId: string }>(`${API_URL}/admin/projects/${id}/retry-syntax`, {}, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },

  /**
   * Detecta y marca proyectos atascados en 'building' sin job activo
   *
   * @remarks
   * Equivalente al check de startup pero invocable manualmente.
   * Útil después de un reinicio de backend para limpiar estados inconsistentes.
   *
   * @returns Número de proyectos corregidos y sus IDs
   */
  async reconcileStuck(): Promise<{ fixed: number; details: string[] }> {
    const token = getAuthToken();
    const response = await axios.post<{ fixed: number; details: string[] }>(
      `${API_URL}/admin/projects/reconcile-stuck`,
      {},
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    return response.data;
  },

  /**
   * Verifica cobertura de la Red Urbana Nacional para un scope territorial
   * sin crear ningún proyecto. Llama a POST /admin/projects/national-coverage-check.
   *
   * @remarks
   * Los campos del DTO siguen la convención del backend:
   * - `territorialScopeType` (no `scopeType`)
   * - `adminLevel` (no `scopeAdminLevel`)
   * - `bounds` como { west, south, east, north } solo para custom_bbox
   */
  async nationalCoverageCheck(dto: {
    countryCode: string;
    territorialScopeType: NationalScopeType;
    adminLevel?: 1 | 2;
    scopeBoundaryCode?: string;
    scopeBoundaryCodes?: string[];
    bounds?: { west: number; south: number; east: number; north: number };
  }): Promise<NationalCoverageResult> {
    const token = getAuthToken();
    const response = await axios.post<NationalCoverageResult>(
      `${API_URL}/admin/projects/national-coverage-check`,
      dto,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    return response.data;
  },

  /**
   * Obtiene datos de monitoreo de sintaxis para un proyecto (SuperAdmin)
   */
  async getSyntaxMonitor(id: string): Promise<SyntaxMonitorData> {
    const token = getAuthToken();
    const response = await axios.get<SyntaxMonitorData>(`${API_URL}/admin/projects/${id}/syntax-monitor`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },
};

// ────────────────────────────────────────────────────────────────────────────
// Types for SyntaxMonitorData (mirror del backend)
// ────────────────────────────────────────────────────────────────────────────

export interface SyntaxMonitorData {
  project: {
    id: string;
    name: string;
    pipelineStatus: string;
    baseNetworkStatus: string;
    urbanNetworkStatus: string;
    syntaxStatus: string;
    syntaxVersion: number;
    syntaxCalculatedAt: string | null;
    pipelineError: string | null;
  };
  network: {
    canonicalEdgesCount: number;
    canonicalNodesCount: number;
  };
  syntaxRun: {
    runId: string;
    status: string;
    engineJobId: string | null;
    engineStatus: string | null;
    monitorFailures: number;
    createdAt: string;
    startedAt: string | null;
    completedAt: string | null;
    errorMessage: string | null;
    engineLastContactAt: string | null;
    edgesProcessed: number | null;
    computationTimeMs: number | null;
  } | null;
  engine: {
    available: boolean;
    url: string;
    jobStatus: string | null;
    jobProgress: number | null;
    jobCurrentPhase: string | null;
    jobError: string | null;
    checkedAt: string;
  };
  queue: {
    activeCount: number;
    waitingCount: number;
    delayedCount: number;
    failedCount: number;
    completedCount: number;
  };
  metricsCoverage: {
    expectedEdges: number;
    totalRows: number;
    ncGlobal: number;
    choiceGlobal: number;
    nainGlobal: number;
    nachGlobal: number;
    ncR1200: number;
    choiceR1200: number;
    nainR1200: number;
    nachR1200: number;
    ncR800: number;
    choiceR800: number;
    nainR800: number;
    nachR800: number;
    coveragePercent: number;
    isComplete: boolean;
  };
  derivedStatus: {
    operativeStatus: 'network_pending' | 'network_ready' | 'network_failed';
    analyticStatus:
      | 'syntax_pending'
      | 'syntax_queued'
      | 'syntax_processing'
      | 'syntax_ready'
      | 'syntax_failed'
      | 'syntax_orphaned'
      | 'syntax_incomplete';
    health:
      | 'healthy'
      | 'slow_but_active'
      | 'engine_offline'
      | 'no_recent_contact'
      | 'orphaned_run'
      | 'failed';
    recommendedAction:
      | 'none'
      | 'wait'
      | 'retry_syntax'
      | 'start_engine'
      | 'clean_orphaned_run'
      | 'inspect_logs';
  };
}
